<template>
  <div
    class="location-page-header d-flex justify-space-between align-end"
    data-testid="setup-location__header"
  >
    <div class="location-page-header__left">
      <div class="location-page-header__title-prefix" data-testid="setup-location__title-prefix">
        Setup
      </div>
      <div
        class="location-page-header__title-group d-flex align-center"
        data-testid="setup-location__title-group"
      >
        <template v-if="!isEditingTitle">
          <h3
            class="location-page-header__title"
            data-testid="setup-location__title"
          >
            {{ title }}
          </h3>
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                class="shadow-off"
                :disabled="loading"
                :loading="loading"
                data-testid="setup-location__refresh-btn"
                @click="$emit('refresh')"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Refresh</span>
          </v-tooltip>
          <v-tooltip v-if="canEditTitle" bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                class="shadow-off"
                :disabled="loading || titleSaving"
                data-testid="setup-location__edit-title-btn"
                @click="startEditTitle"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Edit title</span>
          </v-tooltip>
        </template>

        <template v-else>
          <input
            ref="titleInput"
            v-model="titleDraft"
            type="text"
            class="location-page-header__title-input text-transform-none"
            maxlength="60"
            :disabled="titleSaving"
            data-testid="setup-location__title-input"
            @keydown.enter.prevent="confirmEditTitle"
            @keydown.esc.prevent="cancelEditTitle"
          />
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                class="shadow-off"
                color="primary"
                :disabled="titleSaving || !canSaveTitle"
                :loading="titleSaving"
                data-testid="setup-location__save-title-btn"
                @click="confirmEditTitle"
              >
                <v-icon small>mdi-check</v-icon>
              </v-btn>
            </template>
            <span>Save</span>
          </v-tooltip>
          <v-tooltip bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                small
                class="shadow-off"
                :disabled="titleSaving"
                data-testid="setup-location__cancel-title-btn"
                @click="cancelEditTitle"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Cancel</span>
          </v-tooltip>
        </template>
      </div>

      <p
        class="location-page-header__description"
        data-testid="setup-location__subtitle"
      >
        {{ subtitle }}
      </p>

      <p
        v-if="lastSyncLabel"
        class="location-page-header__last-sync mt-2 mb-0"
        data-testid="setup-location__last-sync"
      >
        Last sync: <span data-testid="setup-location__last-sync-value">{{ lastSyncLabel }}</span>
      </p>
    </div>

    <div
      class="location-page-header__actions d-flex align-center"
      data-testid="setup-location__actions"
    >
      <v-btn
        v-if="showErpSync"
        outlined
        class="location-page-header__erp-btn text-none"
        :disabled="loading"
        data-testid="setup-location__sync-erp-btn"
        @click="$emit('sync-erp')"
      >
        <v-icon left small data-testid="setup-location__sync-erp-icon">mdi-file-sync-outline</v-icon>
        Update From ERP
      </v-btn>

      <template v-if="showToolbarActions">
        <v-btn
          v-if="hasActiveTableFilter"
          text
          class="location-page-header__clear-filter text-none"
          :disabled="loading"
          data-testid="setup-location__clear-filter-btn"
          @click="$emit('clear-filter')"
        >
          Clear Filter
        </v-btn>

        <CustomizeColumnMenu
          v-model="customizeMenuOpen"
          :headers="headers"
          color="caccounting"
          :show-save-button="false"
          :show-apply-button="true"
          data-testid-prefix="setup-location"
          @apply="onApplyColumns"
        />

        <ActivityLog
          consolidated
          use-mdi-history
          :module-name="activityLogModuleName"
          :reference-name-map="referenceNameMap"
          data-testid="setup-location__activity-log"
        />

        <v-tooltip bottom content-class="tooltip-bottom">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              icon
              :disabled="loading"
              data-testid="setup-location__export-btn"
              @click="$emit('export')"
            >
              <img
                class="location-page-header__export-icon"
                src="/img/icons/DownloadIcon.svg"
                alt="Export"
              />
            </v-btn>
          </template>
          <span>Export</span>
        </v-tooltip>
      </template>

      <v-tooltip
        v-if="canAdd && showAddButton"
        bottom
        content-class="tooltip-bottom"
        open-delay="500"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            color="primary"
            class="location-page-header__add-btn px-4 text-none white--text"
            :disabled="loading"
            data-testid="setup-location__add-btn"
            @click="$emit('add-location')"
          >
            <v-icon left small data-testid="setup-location__add-icon">mdi-plus</v-icon>
            {{ title }}
          </v-btn>
        </template>
        <span>Add {{ title }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import './LocationPageHeader.scss';
import CustomizeColumnMenu from '@/modules/financialclose/components/CustomizeColumnMenu/CustomizeColumnMenu.vue';
import ActivityLog from '@/modules/financialclose/components/ActivityLog/ActivityLog.vue';
import { ACTIVITY_LOG_MODULE_NAME } from '../../utils/locationConstants';

export default {
  name: 'LocationPageHeader',

  components: {
    CustomizeColumnMenu,
    ActivityLog,
  },

  props: {
    title: {
      type: String,
      default: 'Location',
    },
    subtitle: {
      type: String,
      default:
        'Create and manage locations to organize countries, types, and thresholds for your financial close cycles.',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    canAdd: {
      type: Boolean,
      default: false,
    },
    canEditTitle: {
      type: Boolean,
      default: false,
    },
    titleSaving: {
      type: Boolean,
      default: false,
    },
    showAddButton: {
      type: Boolean,
      default: true,
    },
    showToolbarActions: {
      type: Boolean,
      default: true,
    },
    hasActiveTableFilter: {
      type: Boolean,
      default: false,
    },
    showErpSync: {
      type: Boolean,
      default: false,
    },
    lastSyncLabel: {
      type: String,
      default: '',
    },
    headers: {
      type: Array,
      default: () => [],
    },
    referenceNameMap: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      customizeMenuOpen: false,
      isEditingTitle: false,
      titleDraft: '',
      activityLogModuleName: ACTIVITY_LOG_MODULE_NAME,
    };
  },

  computed: {
    canSaveTitle() {
      const next = String(this.titleDraft || '').trim();
      return !!next && next !== String(this.title || '').trim();
    },
  },

  watch: {
    title(val) {
      if (!this.isEditingTitle) {
        this.titleDraft = val || '';
        return;
      }
      if (
        !this.titleSaving &&
        String(val || '').trim() === String(this.titleDraft || '').trim()
      ) {
        this.isEditingTitle = false;
      }
    },
    titleSaving(saving, wasSaving) {
      if (
        wasSaving &&
        !saving &&
        this.isEditingTitle &&
        String(this.title || '').trim() === String(this.titleDraft || '').trim()
      ) {
        this.isEditingTitle = false;
      }
    },
  },

  methods: {
    onApplyColumns(nextHeaders) {
      this.customizeMenuOpen = false;
      this.$emit('apply-columns', nextHeaders);
    },

    startEditTitle() {
      this.titleDraft = this.title || '';
      this.isEditingTitle = true;
      this.$nextTick(() => {
        const el = this.$refs.titleInput;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },

    cancelEditTitle() {
      if (this.titleSaving) return;
      this.isEditingTitle = false;
      this.titleDraft = this.title || '';
    },

    confirmEditTitle() {
      if (this.titleSaving) return;
      const next = String(this.titleDraft || '').trim();
      if (!next) return;
      if (next === String(this.title || '').trim()) {
        this.isEditingTitle = false;
        return;
      }
      this.$emit('update-title', next);
    },
  },
};
</script>
